/**
 * 個人スマホ用アプリの「ホーム画面に追加」まわり。
 *
 * Webアプリは自分でホーム画面に追加できない（OSの制約）。Android の Chrome だけは
 * beforeinstallprompt を捕まえておけば、ボタン1つでインストール画面を出せる。
 * このイベントはページ読み込み直後に一度だけ来るので、起動時（main.tsx）に登録しておく。
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

export function initInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // ブラウザ任せの小さなバーではなく、画面の案内から出す
    deferred = e as BeforeInstallPromptEvent;
    listeners.forEach(fn => fn());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach(fn => fn());
  });
}

export function canPromptInstall(): boolean { return !!deferred; }

export function onInstallPromptChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Android Chrome のインストール画面を出す。出せたら true */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const ev = deferred;
  deferred = null; // 1回しか使えない
  try {
    await ev.prompt();
    const choice = await ev.userChoice;
    return choice.outcome === 'accepted';
  } catch { return false; } finally { listeners.forEach(fn => fn()); }
}

/** すでにホーム画面のアイコンから（アプリとして）開いているか */
export function isStandalone(): boolean {
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
  } catch { /* noop */ }
  return (navigator as unknown as { standalone?: boolean }).standalone === true;
}

export function isIOS(): boolean {
  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** 船内端末のAPK（Android WebView）か。ここでは案内を出さない */
export function isNativeShell(): boolean {
  return /; wv\)/.test(navigator.userAgent || '');
}
